import { EditorView, lineNumbers, highlightSpecialChars, drawSelection, keymap, Decoration, highlightActiveLine, gutter, GutterMarker } from "@codemirror/view";
import { EditorState, StateField, StateEffect, EditorSelection, Compartment } from "@codemirror/state";
// Ctrl+F inside an editor searches the editor rather than falling through to the browser's
// page find. The browser's own find is the wrong tool here on two counts: it cannot see text
// CodeMirror has scrolled out of the viewport and never rendered, and in an editable body it
// leaves the caret sitting in the document, so the search term gets typed into the cheat.
import { search, searchKeymap } from "@codemirror/search";
import { unifiedMergeView } from "@codemirror/merge";
import { markdown, markdownLanguage } from "@codemirror/lang-markdown";
import { languages } from "@codemirror/language-data";
import { cpp } from "@codemirror/lang-cpp";
import { defaultHighlightStyle, syntaxHighlighting, LanguageDescription} from "@codemirror/language";
import { history, defaultKeymap, historyKeymap } from "@codemirror/commands";
import { CMThemeEditable, CMThemeReadOnly, CMThemeReadOnlyLarge } from './CodeMirrorTheme';

const customLanguages = languages.concat([
  LanguageDescription.of({
    name: "hlsl",
    alias: ["hlsl"], // optional
    load: async () => cpp()
  }),
  LanguageDescription.of({
    name: "glsl",
    alias: ["glsl"],
    load: async () => cpp()
  }),
  LanguageDescription.of({
    name: "DirectX12",
    alias: ["DirectX12", "DX12"],
    load: async () => cpp()
  }),
  LanguageDescription.of({
    name: "DirectX11",
    alias: ["DirectX11", "DX11"],
    load: async () => cpp()
  }),
  LanguageDescription.of({
    name: "Cg",
    alias: ["Cg"],
    load: async () => cpp()
  })
]);

const md = markdown({
  base: markdownLanguage,
  codeLanguages: customLanguages
});

const CMDecorationIsolation = EditorView.theme({
  // Disable syntax highlighting inside our custom decorations
  ".cm-content .note span, .cm-content .var span, .cm-content .link span": {
    color: "inherit !important",
    fontStyle: "inherit !important",
    fontWeight: "inherit !important",
    textDecoration: "none !important",
  }
});

//#region highlight
const addHighlight = StateEffect.define();
const removeHighlight = StateEffect.define();

export function createHighlightField(ref) {
	return StateField.define({
		create() {
			return Decoration.none;
		},
		update(deco, tr) {
			const oldDocLength = tr.startState.doc.length;

			// Map decorations safely, ignoring links
			if (deco.size > 0 && tr.docChanged) {
				deco = deco.map(tr.changes, (from, to, value) => {
					if (value.spec.class === "link") return value;
					const safeFrom = Math.min(from, oldDocLength - 1);
					const safeTo = Math.min(to, oldDocLength);
					return safeFrom < safeTo ? { from: safeFrom, to: safeTo, spec: value.spec } : null;
				});
			}

			// Apply effects
			for (let e of tr.effects) {
				if (e.is(addHighlight)) {
					if (!e.value.spec || e.value.spec.class !== "link") {
						deco = deco.update({ add: [e.value], sort: true });
					}
				} else if (e.is(removeHighlight)) {
					const from = e.value.from ?? null;
					const to = e.value.to ?? null;
					if (from !== null && to !== null) {
						deco = deco.update({
							filter: (decFrom, decTo) => decTo <= from || decFrom >= to
						});
					}
				}
			}

			// Apply current highlight for typing dynamically
			if (ref.view.currentHighlight && tr.docChanged) {
				tr.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
					if (!inserted || inserted.length === 0) return;

					// fromB is the position where the insertion happened in the new document
					const start = fromB;
					const end = fromB + inserted.length;

					if (start < end) {
						const decoNew = Decoration.mark({ class: ref.view.currentHighlight, inclusiveEnd: true }).range(start, end);
						deco = deco.update({ add: [decoNew], sort: true });
					}
				});
			}

			return deco;
		},
		provide: f => EditorView.decorations.from(f)
	});
}

// --------------------
// removeHighlight clears any decoration that merely overlaps its range, not just the
// overlapping portion — so a highlight that only partially overlaps the target range would
// otherwise vanish entirely. This rebuilds the parts of such decorations that fall outside
// the range so they can be re-added after the clear.
// --------------------
function preserveHighlightFragments(view, from, to) {
	const preserved = [];
	view.state.field(view.highlightField).between(from, to, (start, end, deco) => {
		if (deco.spec.class === "link") return;
		if (start < from) preserved.push(addHighlight.of(Decoration.mark({ class: deco.spec.class, inclusiveEnd: true }).range(start, from)));
		if (end > to) preserved.push(addHighlight.of(Decoration.mark({ class: deco.spec.class, inclusiveEnd: true }).range(to, end)));
	});
	return preserved;
}

// --------------------
// Highlight a selection safely
// --------------------
export function highlightSelection(view, className) {
	const { from, to } = view.state.selection.main;
	const docLength = view.state.doc.length;

	if (from === to) {
		// Cursor at end → future typing highlight
		view.currentHighlight = className;
		return;
	}

	const safeFrom = Math.min(from, docLength - 1);
	const safeTo = Math.min(to, docLength);

	if (safeFrom >= safeTo) return; // nothing to highlight

	const preserved = preserveHighlightFragments(view, safeFrom, safeTo);

	// Remove existing highlights in this range, then restore the parts outside it
	// and add the new highlight.
	const deco = Decoration.mark({ class: className, inclusiveEnd: true }).range(safeFrom, safeTo);
	view.dispatch({
		effects: [removeHighlight.of({ from: safeFrom, to: safeTo }), ...preserved, addHighlight.of(deco)]
	});
}

// --------------------
// Remove highlight from selection and future typing
// --------------------
export function removeHighlightSelection(view) {
	const { from, to } = view.state.selection.main;

	if (from !== to) {
		const preserved = preserveHighlightFragments(view, from, to);
		view.dispatch({ effects: [removeHighlight.of({ from, to }), ...preserved] });
	}

	view.currentHighlight = null;
}

// --------------------
// Apply current highlight to typing dynamically
// --------------------

export function createApplyCurrentHighlight(ref) {
	return EditorView.inputHandler.of((view, from, to, text) => {
		if (!ref.view.currentHighlight || !text) return false;

		const docLength = view.state.doc.length;
		const start = Math.min(from, docLength);
		const end = Math.min(from + text.length, docLength);

		if (start < end) {
			const deco = Decoration.mark({ class: ref.view.currentHighlight, inclusiveEnd: true }).range(start, end);
			view.dispatch({ effects: addHighlight.of(deco) });
		}

		return false; // allow normal typing
	});
}

function saveEditorState(view) {
	if (!view) return null;

	return {
		selection: view.state.selection,
		scrollTop: view.scrollDOM.scrollTop,
		scrollLeft: view.scrollDOM.scrollLeft
	};
}

function restoreFocus(view, savedState) {
	if (!view || !savedState || !savedState.selection) return;

	const sel = savedState.selection;

	// Ensure ranges exist
	let ranges = Array.isArray(sel.ranges) && sel.ranges.length > 0
		? sel.ranges.map(r => ({
			anchor: Math.min(r.anchor, view.state.doc.length),
			head: Math.min(r.head, view.state.doc.length)
		}))
		: [{ anchor: 0, head: 0 }]; // fallback if empty

	// Clamp mainIndex
	let mainIndex = Math.min(sel.mainIndex ?? 0, ranges.length - 1);

	view.focus();
	view.dispatch({
		selection: { ranges, mainIndex },
		scrollIntoView: true
	});

	// Restore scroll safely
	view.scrollDOM.scrollTop = savedState.scrollTop ?? 0;
	view.scrollDOM.scrollLeft = savedState.scrollLeft ?? 0;
}

//#endregion

function exportJSON(view) {
	const doc = view.state.doc.toString();
	const highlights = [];
	// Typing with a highlight active adds one decoration per keystroke, so adjacent/overlapping
	// same-class decorations are merged here to keep the saved array from growing per character.
	view.state.field(view.highlightField).between(0, doc.length, (from, to, deco) => {
		const className = deco.spec.class;
		const last = highlights[highlights.length - 1];
		if (last && last.className === className && from <= last.to) {
			last.to = Math.max(last.to, to);
		} else {
			highlights.push({ from, to, className });
		}
	});
	const links = [];
	view.state.field(linkField).between(0, doc.length, (from, to, deco) => {
		links.push({ from, to, href: deco.spec.attributes?.["data-href"] ?? "" });
	});
	return { text: doc, highlights, links };
}

function importJSON(view, json) {
	// If it's a string, try parsing it
	if (typeof json === "string") {
		try {
			json = JSON.parse(json);
		} catch (e) {
			json = {};
		}
	}

	// If it's not an object after parsing, fallback to empty
	if (!json || typeof json !== "object") {
		console.warn("importJSON: passed value is not an object, using empty content.");
		json = {};
	}

	// Ensure required fields
	const text = typeof json.text === "string" ? json.text : "";
	const highlights = Array.isArray(json.highlights) ? json.highlights : [];
	const links = Array.isArray(json.links) ? json.links : [];

	const changes = { from: 0, to: view.state.doc.length, insert: text };
	const effects = [];

	for (const h of highlights) {
		if (h && typeof h.from === "number" && typeof h.to === "number" && typeof h.className === "string") {
			const deco = Decoration.mark({ class: h.className, inclusiveEnd: true }).range(h.from, h.to);
			effects.push(addHighlight.of(deco));
		}
	}

	for (const l of links) {
		if (l && typeof l.from === "number" && typeof l.to === "number" && typeof l.href === "string") {
			const deco = Decoration.mark({
				tagName: "span",
				attributes: {
					"data-href": l.href,
					"title": l.href
				},
				class: "link"
			}).range(l.from, l.to);
			effects.push(addLinkEffect.of(deco));
		}
	}

	view.dispatch({ changes, effects });
}

const addLinkEffect = StateEffect.define();
const removeLinkEffect = StateEffect.define();

const linkField = StateField.define({
	create() {
		return Decoration.none;
	},
	update(links, tr) {
		// Map decorations over document changes
		links = links.map(tr.changes);

		for (let e of tr.effects) {
			if (e.is(addLinkEffect)) {
				links = links.update({ add: [e.value], sort: true });
			} else if (e.is(removeLinkEffect)) {
				// Remove the exact decoration object
				links = links.update({ filter: (from, to, deco) => deco !== e.value });
			}
		}

		return links;
	},
	provide: f => EditorView.decorations.from(f)
});

async function showPrompt(message, defaultValue = "") {
    return new Promise(resolve => {
        const modal = document.createElement("div");
        modal.className = "cm-prompt-overlay";
        modal.innerHTML = `
            <div class="cm-prompt-dialog">
                <label>${message}</label>
                <input type="text" placeholder="${defaultValue}">
                <div class="cm-prompt-buttons">
                    <a role="button" class="ok">OK</a>
                    <a role="button" class="cancel">Cancel</a>
                </div>
            </div>
        `;
        document.body.appendChild(modal);

        const input = modal.querySelector("input");
        input.focus();

        modal.querySelector(".ok").onclick = () => {
            const val = input.value.trim();
            modal.remove();
            resolve(val);
        };

        modal.querySelector(".cancel").onclick = () => {
            modal.remove();
            resolve(null);
        };
    });
}

// Combined text+URL prompt used for both creating and editing a link, so both fields are
// confirmed together with a single OK instead of two sequential prompts.
async function showLinkPrompt(textDefault = "", urlDefault = "https://") {
    return new Promise(resolve => {
        const modal = document.createElement("div");
        modal.className = "cm-prompt-overlay";
        modal.innerHTML = `
            <div class="cm-prompt-dialog">
                <div class="cm-prompt-field">
                    <label>Link text</label>
                    <input type="text" class="cm-link-text" placeholder="A cool webpage!">
                </div>
                <div class="cm-prompt-field">
                    <label>URL</label>
                    <input type="text" class="cm-link-url" placeholder="https://">
                </div>
                <div class="cm-prompt-buttons">
                    <a role="button" class="ok">OK</a>
                    <a role="button" class="cancel">Cancel</a>
                </div>
            </div>
        `;
        document.body.appendChild(modal);

        const textInput = modal.querySelector(".cm-link-text");
        const urlInput = modal.querySelector(".cm-link-url");
        textInput.value = textDefault;
        urlInput.value = urlDefault;
        textInput.focus();
        textInput.select();

        const submit = () => {
            const text = textInput.value.trim();
            const url = urlInput.value.trim();
            modal.remove();
            resolve({ text, url });
        };

        const cancel = () => {
            modal.remove();
            resolve(null);
        };

        textInput.addEventListener("keydown", e => {
            if (e.key === "Enter") {
                e.preventDefault();
                urlInput.focus();
            }
        });
        urlInput.addEventListener("keydown", e => {
            if (e.key === "Enter") {
                e.preventDefault();
                submit();
            }
        });

        modal.querySelector(".ok").onclick = submit;
        modal.querySelector(".cancel").onclick = cancel;
    });
}

function normalizeUrl(url) {
    return /^https?:\/\//i.test(url) ? url : "https://" + url;
}

async function handleLinkButton(view) {
	const prevHighlight = view.currentHighlight; // save current highlight
	view.currentHighlight = null; // disable highlight while inserting/editing link

	const sel = view.state.selection.main;
	const from = Math.min(sel.from, sel.to);
	const to = Math.max(sel.from, sel.to);

	const linkDecos = view.state.field(linkField);
	let linksInSelection = [];

	linkDecos.between(from, to, (start, end, deco) => {
		if (deco.spec.attributes?.["data-href"]) {
			linksInSelection.push({ from: start, to: end, deco });
		}
	});

	if (from === to && linksInSelection.length === 0) {
		const result = await showLinkPrompt("", "https://");
		if (!result || !result.text || !result.url) {
			view.currentHighlight = prevHighlight;
			return;
		}

		const { text } = result;
		const formattedUrl = normalizeUrl(result.url);

		view.dispatch({ changes: { from, to, insert: text } });

		const deco = Decoration.mark({
			tagName: "span",
			attributes: {
				"data-href": formattedUrl,
				"title": formattedUrl
			},
			class: "link"
		}).range(from, from + text.length);

		view.dispatch({ effects: addLinkEffect.of(deco) });
		view.currentHighlight = prevHighlight;
		return;
	}

	if (from !== to && linksInSelection.length === 0) {
		const url = await showPrompt("Enter URL for selection:", "https://");
		if (!url) {
			view.currentHighlight = prevHighlight;
			return;
		}

		const deco = Decoration.mark({
			tagName: "span",
			attributes: {
				"data-href": url,
				"title": url
			},
			class: "link"
		}).range(from, to);

		view.dispatch({ effects: addLinkEffect.of(deco) });
		view.currentHighlight = prevHighlight;
		return;
	}

	if (linksInSelection.length > 0) {
		const link = from === to
			? linksInSelection.find(l => from >= l.from && from <= l.to)
			: linksInSelection.find(l => l.from < to && l.to > from);

		if (!link) {
			view.currentHighlight = prevHighlight;
			return;
		}

		const currentText = view.state.doc.sliceString(link.from, link.to);
		const currentUrl = link.deco.spec.attributes["data-href"];

		const result = await showLinkPrompt(currentText, currentUrl);

		if (result === null) {
			view.currentHighlight = prevHighlight;
			return;
		}

		if (!result.text || !result.url) {
			// Either field left empty removes the link.
			view.dispatch({ effects: removeLinkEffect.of(link.deco) });
		} else {
			const newUrl = normalizeUrl(result.url);
			const textChanged = result.text !== currentText;

			if (textChanged) {
				view.dispatch({ changes: { from: link.from, to: link.to, insert: result.text } });
			}

			const newDeco = Decoration.mark({
				tagName: "span",
				attributes: {
					"data-href": newUrl,
					"title": newUrl
				},
				class: "link"
			}).range(link.from, link.from + result.text.length);

			view.dispatch({
				effects: [
					removeLinkEffect.of(link.deco),
					addLinkEffect.of(newDeco)
				]
			});
		}

		view.currentHighlight = prevHighlight; // restore highlight after editing
	}
}

const linkClickHandler = EditorView.domEventHandlers({
	click: (event, view) => {
		const target = event.target;
		if (target && target.dataset?.href) {
			const url = target.dataset.href;
			window.open(url, "_blank"); // open in new tab
			return true; // prevent default
		}
		return false;
	},
	// Middle-click starts the browser's native autoscroll on mousedown, before auxclick ever
	// fires — has to be prevented here, not in the auxclick handler below, or it's too late.
	mousedown: (event, view) => {
		if (event.button !== 1) return false;
		const target = event.target;
		if (target && target.dataset?.href) {
			event.preventDefault();
			return true;
		}
		return false;
	},
	// Middle-click fires `auxclick`, not `click`, since the link is a <span>, not a real <a href>.
	auxclick: (event, view) => {
		if (event.button !== 1) return false; // only the middle mouse button
		const target = event.target;
		if (target && target.dataset?.href) {
			const url = target.dataset.href;
			window.open(url, "_blank");
			event.preventDefault();
			return true;
		}
		return false;
	},
});

function escapeHTML(str) {
	return str.replace(/[&<>"']/g, (ch) =>
	({
		"&": "&amp;",
		"<": "&lt;",
		">": "&gt;",
		'"': "&quot;",
		"'": "&#39;"
	}[ch])
	);
}

// Renders the selection as plain text, but with each linked range written out as a Markdown
// link (`[text](href)`) instead of bare text, so pasting a cheat's link into something that only
// accepts plain text (chat, notes, another app) doesn't silently drop the URL. Walked off
// linkField directly rather than piggybacking on copySelectionWithDecos' merged boundary list,
// since a highlight decoration starting or ending in the middle of a link would otherwise split
// one link into several adjacent `[..](href)[..](href)` fragments.
function plainTextWithMarkdownLinks(view, from, to) {
	const doc = view.state.doc;
	const linkRanges = [];

	view.state.field(linkField).between(from, to, (start, end, deco) => {
		if (end > from && start < to) {
			const decoFrom = Math.max(start, from);
			const decoTo = Math.min(end, to);
			const href = deco.spec.attributes?.["data-href"] ?? "";
			linkRanges.push({ from: decoFrom, to: decoTo, href });
		}
	});
	linkRanges.sort((a, b) => a.from - b.from);

	let plain = "";
	let pos = from;
	for (const r of linkRanges) {
		if (r.from > pos) plain += doc.sliceString(pos, r.from);
		const linkText = doc.sliceString(r.from, r.to);
		plain += r.href ? `[${linkText}](${r.href})` : linkText;
		pos = r.to;
	}
	if (pos < to) plain += doc.sliceString(pos, to);

	return plain;
}

// ---------- improved copy that preserves overlapping decos ----------
function copySelectionWithDecos(view) {
	const sel = view.state.selection.main;
	let { from, to } = sel;
	if (from === to) return { html: "", plain: "" };

	const doc = view.state.doc;
	const plain = plainTextWithMarkdownLinks(view, from, to);

	// Collect decorations from both fields
	const ranges = [];
	view.state.field(linkField).between(from, to, (start, end, deco) => {
		if (end > from && start < to) {
			// Clip the decoration to selection boundaries
			const decoFrom = Math.max(start, from);
			const decoTo = Math.min(end, to);
			ranges.push({ from: decoFrom, to: decoTo, deco });
		}
	});
	view.state.field(view.highlightField).between(from, to, (start, end, deco) => {
		if (end > from && start < to) {
			// Clip the decoration to selection boundaries
			const decoFrom = Math.max(start, from);
			const decoTo = Math.min(end, to);
			ranges.push({ from: decoFrom, to: decoTo, deco });
		}
	});

	// Create boundary events
	const events = [];
	for (const r of ranges) {
		events.push({ pos: r.from, type: "start", deco: r.deco });
		events.push({ pos: r.to, type: "end", deco: r.deco });
	}

	// Sort boundaries by position, then end before start at same pos
	events.sort((a, b) => a.pos - b.pos || (a.type === "end" ? -1 : 1));

	let html = "";
	let active = [];
	let pos = from;
	let i = 0;

	while (pos < to) {
		// Process all events at this boundary first
		while (i < events.length && events[i].pos === pos) {
			if (events[i].type === "start") {
				active.push(events[i].deco);
			} else {
				active = active.filter(d => d !== events[i].deco);
			}
			i++;
		}

		// Next boundary (or end of selection)
		let next = to;
		if (i < events.length) next = Math.min(next, events[i].pos);

		if (next > pos) {
			const text = doc.sliceString(pos, next);

			let wrapped = escapeHTML(text);
			for (const deco of [...active].reverse()) {
				if (deco.spec.class === "link") {
					// A real anchor rather than a span: this is what lets the "text/html" flavor
					// paste as a working link outside the app (Slack, Notion, Gmail, Word), not
					// just round-trip through our own paste handler below, which still reads
					// data-href the same as before.
					const href = deco.spec.attributes?.["data-href"] ?? "";
					wrapped = `<a href="${escapeHTML(href)}" data-href="${escapeHTML(href)}" class="link">${wrapped}</a>`;
				} else if (deco.spec.class) {
					wrapped = `<span class="${escapeHTML(deco.spec.class)}">${wrapped}</span>`;
				}
			}

			html += wrapped;
			pos = next;
		}
	}

	return { html, plain };
}

// ---------- unified copy + paste handlers ----------
const clipboardHandlers = EditorView.domEventHandlers({
	copy(event, view) {
		const { html, plain } = copySelectionWithDecos(view);

		// set both so external apps and our paste handler have data
		event.clipboardData.setData("text/plain", plain);
		event.clipboardData.setData("text/html", html);
		event.preventDefault();
	},

	paste(event, view) {
		const clipboard = event.clipboardData;
		if (!clipboard) return false; // let default

		const html = clipboard.getData("text/html");
		const textPlain = clipboard.getData("text/plain");

		// If there's no HTML we can parse, fall back to plain text paste (default)
		if (!html) return false;

		event.preventDefault();

		// Parse HTML and reconstruct plain text + ranges for decos.
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const body = doc.body;

		let text = "";
		const highlights = []; // { from, to, className }
		const links = [];      // { from, to, href }

		// walk DOM recursively to build text and record ranges
		let count = 0;
		function walk(node) {
			if (node.nodeType === 3) { // TEXT_NODE
				const start = text.length;

				let nodeValue = node.nodeValue;
				if(count == 0)
				{
					nodeValue = nodeValue.replace(/^[\r\n\u2028\u2029]/, "");
					count++;
				}
				text += nodeValue || "";

				return { start, end: text.length };
			}

			if (node.nodeType !== 1) return { start: text.length, end: text.length };

			const tag = node.nodeName.toLowerCase();

			if (tag === "div" && node.classList.contains("cm-line")) {
				let lineStart = text.length;
				for (let i = 0; i < node.childNodes.length; i++) {
					walk(node.childNodes[i]);
				}
				return { start: lineStart, end: text.length };
			}

			const start = text.length;

			// Record span/anchor BEFORE walking children. "a" is included alongside "span" so a
			// link copied out of this same editor (now written as a real <a>, see
			// copySelectionWithDecos above) round-trips back into a link decoration on paste, and
			// so does a plain hyperlink pasted in from outside the app (Slack, Notion, a browser
			// tab) via its ordinary href attribute.
			if (tag === "span" || tag === "a") {
				const href = node.getAttribute("data-href") || node.getAttribute("href");
				const cls = node.getAttribute("class");
				let endPlaceholder = start; // will update after children
				for (let i = 0; i < node.childNodes.length; i++) {
					walk(node.childNodes[i]);
				}
				const end = text.length;
				if (href) links.push({ from: start, to: end, href });
				if (cls) {
					const classes = cls.split(/\s+/).filter(c => c && c !== "link");
					if (classes.length) highlights.push({ from: start, to: end, className: classes.join(" ") });
				}
				return { start, end };
			} else {
				for (let i = 0; i < node.childNodes.length; i++) {
					walk(node.childNodes[i]);
				}
				const end = text.length;
				return { start, end };
			}
		}

		// walk immediate children (preserves top-level structure)
		for (let c = 0; c < body.childNodes.length; c++) {
			walk(body.childNodes[c]);
		}

		// If parsing produced no text (some clipboards wrap differently), fall back
		if (text.length === 0) {
			text = textPlain || "";
		}

		text = text.replace(/\n+$/, "");

		// Insert at exact caret/selection
		const sel = view.state.selection.main;
		const insertFrom = sel.from;
		const insertTo = sel.to;

		const changes = { from: insertFrom, to: insertTo, insert: text };
		const effects = [];

		// create decorations relative to insertFrom
		for (const h of highlights) {
			if (h.to > h.from) {
				const deco = Decoration.mark({ class: h.className, inclusiveEnd: true }).range(insertFrom + h.from, insertFrom + h.to);
				effects.push(addHighlight.of(deco));
			}
		}
		for (const l of links) {
			if (l.to > l.from) {
				const deco = Decoration.mark({
					tagName: "span",
					attributes: {
						"data-href": l.href,
						"title": l.href
					},
					class: "link"
				}).range(insertFrom + l.from, insertFrom + l.to);
				effects.push(addLinkEffect.of(deco));
			}
		}

		view.dispatch({
			changes,
			effects,
			selection: EditorSelection.cursor(insertFrom + text.length)
		});
		return true;
	}
});

function limitLines(maxLines) {
  return EditorState.transactionFilter.of((tr) => {
    if (!tr.docChanged) return tr;

    let newDoc = tr.newDoc;
    let lineCount = newDoc.lines;

    if (lineCount > maxLines) {
      // Reject this transaction
      return [];
    }

    return tr;
  });
}

function limitLineLength(maxLength) {
  return EditorState.transactionFilter.of((tr) => {
    if (!tr.docChanged) return tr;

    const newDoc = tr.newDoc; // Text object
    let exceeded = false;

    for (let i = 1; i <= newDoc.lines; i++) {
      const line = newDoc.line(i).text; // get text of the line
      if (line.length > maxLength) {
        exceeded = true;
        break; // stop checking
      }
    }

    if (exceeded) {
      return []; // reject transaction
    }

    return tr;
  });
}

const blurHandler = EditorView.domEventHandlers({
	blur: (event, view) => {
		// Collapse selection so drawSelection clears
		view.dispatch({
			selection: { anchor: 0 },
			scrollIntoView: false
		});

		return false;
	}
});

//#region checklist (Notes' 'list' category tick-boxes)
// Optional per-line checkbox gutter that public/js/notes.js opts a note's editor into when its
// category is 'list' (see cmAPI.setChecklistMode below) — not used by cheats/Vault, or by Notes
// itself for 'note'/'memory'. Lives in its own Compartment on the view so it can be added,
// removed, and reconfigured (read-only in the browse card vs. clickable in the edit form) after
// the editor already exists, without tearing down and recreating the whole EditorView. Checked
// lines are exchanged with callers as 0-based indices, matching a plain array on the note doc
// (functions/routes/db/notes.js's `checkedLines`); CodeMirror's own Line#number is 1-based, so
// the boundary is entirely inside this region.
const setChecklistLinesEffect = StateEffect.define();
const toggleChecklistLineEffect = StateEffect.define();

const checklistField = StateField.define({
	create: () => new Set(),
	update(value, tr) {
		for (const e of tr.effects) {
			if (e.is(setChecklistLinesEffect)) {
				value = new Set(e.value);
			} else if (e.is(toggleChecklistLineEffect)) {
				value = new Set(value);
				if (value.has(e.value)) value.delete(e.value);
				else value.add(e.value);
			}
		}
		return value;
	}
});

class ChecklistMarker extends GutterMarker {
	constructor(checked) {
		super();
		this.checked = checked;
	}
	eq(other) {
		return other.checked === this.checked;
	}
	toDOM() {
		const box = document.createElement("span");
		box.className = "cm-checklist-box" + (this.checked ? " checked" : "");
		return box;
	}
}

// interactive is fixed for the lifetime of one reconfigure — toggling it means calling
// setChecklistMode again (notes.js does this whenever it switches an existing note's editor
// between browse/edit rendering) rather than mutating a flag the gutter reads live.
function checklistExtensions(interactive, onToggle) {
	return [
		checklistField,
		gutter({
			class: "cm-checklist-gutter",
			lineMarker: (view, line) => new ChecklistMarker(view.state.field(checklistField).has(view.state.doc.lineAt(line.from).number)),
			// Without this, the gutter only recomputes markers when the document/viewport itself
			// changes — our toggle effects change checklistField but leave the doc untouched, so
			// ticking a box would silently update the underlying Set (and the saved note) without
			// ever redrawing the checkbox itself.
			lineMarkerChange: (update) => update.transactions.some(tr => tr.effects.some(e => e.is(setChecklistLinesEffect) || e.is(toggleChecklistLineEffect))),
			initialSpacer: () => new ChecklistMarker(false),
			domEventHandlers: {
				mousedown: (view, line) => {
					if (!interactive) return false;
					const lineNumber = view.state.doc.lineAt(line.from).number;
					view.dispatch({ effects: toggleChecklistLineEffect.of(lineNumber) });
					if (onToggle) {
						const checked = view.state.field(checklistField).has(lineNumber);
						const allLines = [...view.state.field(checklistField)].map(n => n - 1).sort((a, b) => a - b);
						onToggle(lineNumber - 1, checked, allLines);
					}
					return true;
				}
			}
		})
	];
}

// Adds the checklist API to a view that already has a `checklistCompartment` (an empty one,
// included at creation so it can be reconfigured later — see initEditor/initReadOnlyEditor).
// Called unconditionally for every editor; a view that never opts in via setChecklistMode simply
// never has the field/gutter in its extensions, so getChecklistLines stays [] and
// setChecklistLines is a no-op.
function addChecklistAPI(view, api) {
	api.setChecklistMode = (options) => {
		view.dispatch({
			effects: view.checklistCompartment.reconfigure(options ? checklistExtensions(options.interactive, options.onToggle) : [])
		});
	};
	api.setChecklistLines = (lineIndices) => {
		if (!view.state.field(checklistField, false)) return;
		view.dispatch({ effects: setChecklistLinesEffect.of((lineIndices || []).map(i => i + 1)) });
	};
	api.getChecklistLines = () => {
		const value = view.state.field(checklistField, false);
		return value ? [...value].map(n => n - 1).sort((a, b) => a - b) : [];
	};
}
//#endregion

export function initEditor(domElement, initialContent, onChange) {

	const changeListener = EditorView.updateListener.of(update => {
		if (update.docChanged || update.transactions.some(tr => tr.effects.length > 0)) {
			onChange?.();
		}
	});

	const ref = { view: null, currentHighlight: null };
	const highlightField = createHighlightField(ref);
	const applyCurrentHighlight = createApplyCurrentHighlight(ref);
	const checklistCompartment = new Compartment();

	let lastEditorState = null;

	const state = EditorState.create({
		doc: initialContent,
		extensions: [
			lineNumbers(),
			highlightSpecialChars(),
			drawSelection(),
			history(),
			search({ top: true }),
			// Ahead of the default keymap so the search bindings win any overlap.
			keymap.of(searchKeymap),
			keymap.of([...defaultKeymap, ...historyKeymap]),
			syntaxHighlighting(defaultHighlightStyle),
			CMDecorationIsolation,
			highlightActiveLine(),
			md,
			CMThemeEditable,
			highlightField,
			applyCurrentHighlight,
			linkField,
			changeListener,
			clipboardHandlers,
			limitLines(100),
			limitLineLength(600),
			checklistCompartment.of([])
		]
	});

	const view = new EditorView({
		state,
		parent: domElement
	});

	view.checklistCompartment = checklistCompartment;

	// API functions bound to this editor instance
	const api = {
		highlightVar: () => highlightSelection(view, "var"),
		highlightNote: () => highlightSelection(view, "note"),
		highlightClear: () => removeHighlightSelection(view),
		linkButton: async () => await handleLinkButton(view),
		saveEditorState: () => { lastEditorState = saveEditorState(view); },
		restoreFocus: () => restoreFocus(view, lastEditorState),
		exportJSON: () => exportJSON(view),
		importJSON: (json) => importJSON(view, json)
	};
	addChecklistAPI(view, api);

	// attach API to the view itself
	view.cmAPI = api;
	view.highlightField = highlightField;
	view.currentHighlight = null;
	ref.view = view;
	view.ref = ref;

	return view;
}

export function initReadOnlyEditor(domElement, initialContent, large) {

	const ref = { view: null };
	const highlightField = createHighlightField(ref);
	const applyCurrentHighlight = createApplyCurrentHighlight(ref);
	const checklistCompartment = new Compartment();

	const extensions = [
		highlightSpecialChars(),
		drawSelection(),
		// editable:false already stops typing; this is what the search panel reads to leave its
		// Replace fields out, and it keeps every editing command inert rather than merely
		// unreachable. It does not block importJSON below, which dispatches its changes directly.
		EditorState.readOnly.of(true),
		search({ top: true }),
		keymap.of(searchKeymap),
		keymap.of([...defaultKeymap, ...historyKeymap]),
		syntaxHighlighting(defaultHighlightStyle),
		CMDecorationIsolation,
		highlightActiveLine(),
		md,
		highlightField,
		applyCurrentHighlight,
		linkField,
		clipboardHandlers,
		limitLines(100),
		limitLineLength(600),
		linkClickHandler,
		EditorView.editable.of(false),
		EditorView.contentAttributes.of({ tabindex: "0" }),
		blurHandler,
		checklistCompartment.of([])
	];

	// Add line numbers if large is true
	if (large) {
		extensions.unshift(lineNumbers());
		extensions.unshift(CMThemeReadOnlyLarge);
	}
	else {
		extensions.unshift(CMThemeReadOnly);
	}

	const state = EditorState.create({
		doc: initialContent,
		extensions
	});

	const view = new EditorView({
		state,
		parent: domElement
	});

	view.checklistCompartment = checklistCompartment;

	let lastEditorState = null;

	// API functions bound to this editor instance
	const api = {
		saveEditorState: () => { lastEditorState = saveEditorState(view); },
		restoreFocus: () => restoreFocus(view, lastEditorState),
		exportJSON: () => exportJSON(view),
		importJSON: (json) => importJSON(view, json)
	};
	addChecklistAPI(view, api);

	// attach API to the view itself
	view.cmAPI = api;
	view.highlightField = highlightField;
	view.currentHighlight = null;
	ref.view = view;
	view.ref = ref;

	return view;
}

// Shows one revision's code diffed against another, for the revision-history panel's compare
// button. Only plain text is diffed: highlights/links are positions into a single revision's
// text, so they don't carry a meaning across two different revisions and are left out here.
export function initDiffEditor(domElement, newText, oldText, large) {

	const extensions = [
		highlightSpecialChars(),
		drawSelection(),
		EditorState.readOnly.of(true),
		search({ top: true }),
		keymap.of(searchKeymap),
		keymap.of(defaultKeymap),
		syntaxHighlighting(defaultHighlightStyle),
		highlightActiveLine(),
		md,
		clipboardHandlers,
		limitLines(100),
		limitLineLength(600),
		EditorView.editable.of(false),
		EditorView.contentAttributes.of({ tabindex: "0" }),
		blurHandler,
		// mergeControls:false since this is a read-only comparison, not an accept/reject workflow.
		unifiedMergeView({ original: oldText, mergeControls: false, gutter: false })
	];

	if (large) {
		extensions.unshift(lineNumbers());
		extensions.unshift(CMThemeReadOnlyLarge);
	}
	else {
		extensions.unshift(CMThemeReadOnly);
	}

	const state = EditorState.create({
		doc: newText,
		extensions
	});

	const view = new EditorView({
		state,
		parent: domElement
	});

	return view;
}

window.initEditor = initEditor;
window.initReadOnlyEditor = initReadOnlyEditor;
window.initDiffEditor = initDiffEditor;